import { Component, forwardRef, ViewChild, ElementRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IAngularMyDpOptions, IMyDateModel, IMyDate } from 'angular-mydatepicker';
import * as moment from 'moment/moment';

@Component({
  selector: 'app-service-period',
  templateUrl: './service-period.component.html',
  styleUrls: ['./service-period.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ServicePeriodComponent),
      multi: true
    }
  ]
})
export class ServicePeriodComponent implements ControlValueAccessor, OnInit {

  @Input()
  public set minDays(days: number) {
    this._minDays = days;
  }

  @Input()
  public showTooltip: boolean;

  public days: number;

  @ViewChild('start', { read: ElementRef, static: false })
  public startPicker: ElementRef;
  @ViewChild('end', { read: ElementRef, static: false })
  public endPicker: ElementRef;

  public _minDays = 30;

  public get minDays() {
    return this._minDays;
  }

  public startDateOptions: IAngularMyDpOptions;
  public endDateOptions: IAngularMyDpOptions;

  private defaultStartDateOptions: IAngularMyDpOptions = {
    markCurrentDay: true,
    dateFormat: 'mm/dd/yyyy',
    dateRange: false
  };

  private defaultEndDateOptions: IAngularMyDpOptions = {
    markCurrentDay: true,
    dateFormat: 'mm/dd/yyyy',
    dateRange: false
  };

  private changeFn: (val: any) => void;

  public data: ServicePeriod;
  public disabled: boolean;

  public startDate: IMyDateModel;
  public endDate: IMyDateModel;

  ngOnInit() {
    this.startDateOptions = this.defaultStartDateOptions;
    this.endDateOptions = this.defaultEndDateOptions;

    this.endDateOptions.stylesData = {
      selector: 'end',
      styles: `
        .end .myDpSelectorAbsolute { left: 200px !important; z-index: 100; }
        .myDpSelectorArrowLeft:after, .myDpSelectorArrowLeft:before { visibility: hidden; }
      `
    };

    this.startDateOptions.stylesData = {
      selector: 'start',
      styles: `
        .start .myDpSelectorAbsolute { right: 100px !important; z-index: 100; }
        .myDpSelectorArrowLeft:after, .myDpSelectorArrowLeft:before { visibility: hidden; }
      `
    };
  }

  private dateFormatter (date: IMyDateModel): Date{
    const {day, month, year} = date.singleDate.date
    const formattedMonth = `${month-1}`.length > 1 ? month-1: `0${month}`
    const formattedDay = `${day}`.length > 1 ? day: `0${day}`
    return new Date(`${formattedMonth}/${formattedDay}/${year}`)
  }

  public startDateChanged(val: IMyDateModel) {
    if (JSON.stringify(val.singleDate) == JSON.stringify(this.startDate.singleDate)) {
      return
    }

    this.startDate = val;
    if (val) {
      this.data.startDate = val.singleDate.jsDate;
      this.days = moment(this.data.endDate.setHours(0, 0, 0, 0)).diff(moment(this.data.startDate.setHours(0, 0, 0, 0)), 'day', true);
    } else {
      this.data.startDate = null;
    }

    if (this.minDays > 0) {
      this.adjustEndDateOffset();
    }
    if (this.changeFn) {
      this.changeFn(this.data);
    }
  }

  public endDateChanged(val: IMyDateModel) {
    if (JSON.stringify(val.singleDate.date) == JSON.stringify(this.endDate.singleDate.date)) {
      return
    }

    this.endDate = val;
    if (val) {
      this.data.endDate = val.singleDate.jsDate;
      this.days = moment(this.data.endDate.setHours(0, 0, 0, 0)).diff(moment(this.data.startDate.setHours(0, 0, 0, 0)), 'day', true);
    } else {
      this.data.endDate = null;
    }

    if (this.changeFn) {
      this.changeFn(this.data);
    }

  }

  private adjustEndDateOffset() {
    const minEndDate = this.addDays(this.startDate.singleDate.jsDate, this.minDays);
    const daysBetween = moment(this.data.endDate.setHours(0, 0, 0, 0)).diff(moment(this.data.startDate.setHours(0, 0, 0, 0)), 'day', true);

    if (daysBetween < this.minDays) {
      this.endDate = {isRange: false, singleDate: { jsDate: minEndDate }};
      this.data.endDate = this.endDate.singleDate.jsDate;
      this.days = moment(this.data.endDate.setHours(0, 0, 0, 0)).diff(moment(this.data.startDate.setHours(0, 0, 0, 0)), 'day', true);
    }

    this.disableUntil(this.addDays(minEndDate, -1));
  }

  disableUntil(date: Date) {
    const copy = JSON.parse(JSON.stringify(this.endDateOptions));
    copy.disableUntil = { year: date.getFullYear(),
                         month: date.getMonth() + 1,
                         day: date.getDate() };
    this.endDateOptions = copy;
  }

  writeValue(obj: any): void {
    this.data = obj;

    this.startDate = {isRange: false, singleDate: { jsDate: this.data.startDate }};  // set init data
    this.endDate = {isRange: false, singleDate: { jsDate: this.data.endDate }}; // set init data

    if (this.minDays > 0) {
      this.startDateOptions.disableUntil = this.toMyDate(this.addDays(this.startDate.singleDate.jsDate, -1));
      this.endDateOptions.disableUntil = this.toMyDate(this.addDays(this.endDate.singleDate.jsDate, -1));
    }
    this.days = moment(this.data.endDate.setHours(0, 0, 0, 0)).diff(moment(this.data.startDate.setHours(0, 0, 0, 0)), 'day', true);
  }

  private getToday(): Date {
    const now = new Date(Date.now());
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + days);
    return result;
  }

  private toMyDate(date: Date): IMyDate {
      return {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
      };
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
  }
  registerOnTouched(fn: any): void { }

  setDisabledState?(isDisabled: boolean): void {
    // enable/disable is controllerd internally in this component, based on business rules for editing dates of
    this.disabled = isDisabled;
  }
}

export interface ServicePeriod {
  startDate: Date;
  endDate: Date;
  isNew: boolean;
}

